import { AxiosResponse } from 'axios';
import { LdJsonBreadCrumbsResort, LdJsonWeather } from 'components/LdJson';
import { RegularAd } from 'googlead/components/RegularAd';
import { SidebarAd } from 'googlead/components/SidebarAd';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { IResortLayout } from 'page-components/resort/_layout';
import React, { useMemo } from 'react';
import { getResortSlug } from 'util/memcache';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import {
  fetchResortAccommodations,
  fetchResortRelated,
  fetchResortRelatedNews,
  fetchResortShortInfo, fetchResortShortWeatherReport,
  fetchResortSnowReport,
  fetchWeatherInfo,
} from 'api/resort/static';
import {
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IWeatherInfo,
  ResortWeatherType,
} from 'api/resort/types';
import { CurrentLink, CurrentTab } from 'components/resort/ResortHeaderNav/constants';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { BlockContent } from 'components/blocks/BlockContent';
import { ResortWeather } from 'components/resort/ResortWeather';
import { Routes } from 'routes';
import PlanYourTrip from 'components/common/PlanYourTrip/ResortWidget';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import { Hero400Error } from 'components/400';
import { AiAssistantWidget } from 'components/common/AiAssistantWidget';

import { fetchResortMeta } from 'api/region/static';
import { useDistanceFormatter, useTemperatureFormatter } from 'i18n/unitFormat';
import { weatherTypeMap } from 'util/weatherTypeMap';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { mpsToKmh } from 'i18n/units';
import { IAccommodationPage } from 'api/tripadvisor/types';

export type ResortWeatherPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  weatherInfoDaily: IWeatherInfo | null,
  weatherInfoHourly: IWeatherInfo | null,
  relatedNews: IRelatedNews | null,
  accommodations: IAccommodationPage | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-weather-page';
export const TEMPLATE_ID = '2.2.2';
export const TEMPLATE_NAME = 'Resort Weather';

export default function ResortWeatherPage({
  fullResort,
  pathInfo,
  weatherInfoDaily,
  weatherInfoHourly,
  relatedNews,
}: ResortWeatherPageProps) {
  const formatTemperatureFn = useTemperatureFormatter('temperature');
  const formatDistanceFn = useDistanceFormatter('kmhOrMileh');

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  const closestWeatherByHours = useMemo(() => {
    const weatherInfoByHours = weatherInfoHourly?.weatherItems || [];

    return weatherInfoByHours[0] || {};
  }, [weatherInfoHourly]);

  const {
    weatherType, baseTemp, summitTemp, wind,
  } = useMemo(() => ({
    weatherType: closestWeatherByHours.base?.type
      ? t(weatherTypeMap[closestWeatherByHours.base?.type])
      : '',
    baseTemp: formatTemperatureFn(
      [closestWeatherByHours.base?.temp.min, closestWeatherByHours.base?.temp.max],
      '/',
      undefined,
      false,
      true,
    ),
    summitTemp: formatTemperatureFn(
      [closestWeatherByHours.summit?.temp.min, closestWeatherByHours.summit?.temp.max],
      '/',
      undefined,
      false,
      true,
    ),
    wind: closestWeatherByHours.base?.wind.speed ? formatDistanceFn(mpsToKmh(closestWeatherByHours.base.wind.speed), undefined, undefined, true, true) : null,
  }), [closestWeatherByHours, weatherInfoHourly]);

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={t('resort.weather.seoTitle', { resort: fullResort.title })} />
          <BlockContent>
            {m('resort.weather.seoText', {
              resort: fullResort.title,
              wind,
              weather_type: weatherType,
              base_temp: baseTemp,
              summit_temp: summitTemp,
            })}
          </BlockContent>
        </BoxWithShadow>
        <div>
          {weatherInfoHourly && weatherInfoDaily && (
            <ResortWeather
              weatherInfoDaily={weatherInfoDaily}
              weatherInfoHourly={weatherInfoHourly}
              pathInfo={pathInfo}
              aiAssistantWidget={(
                <AiAssistantWidget
                  templateId={TEMPLATE_ID}
                  name={fullResort.title}
                  margin={{ marginTop: '0.5rem', marginBottom: '1.75rem' }}
                />
              )}
            />
          )}
          <RegularAd />
          <PlanYourTrip
            resort={pathInfo}
            hasSkiRental={pathInfo.hasSkiRental}
            hasFlights={pathInfo.hasFlights}
            hasCarRental={pathInfo.hasCarRental}
            isPartner={fullResort.isPartner}
            liftsUrl={fullResort.liftsUrl}
            rentalUrl={fullResort.rentalUrl}
            lessonsUrl={fullResort.lessonsUrl}
            resortCountry={fullResort?.region?.country_code}
          />
        </div>
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>
      <LdJsonBreadCrumbsResort
        resort={pathInfo}
        region={fullResort.region}
        fn={Routes.resortWeather}
      />
      <LdJsonWeather fullResort={fullResort} pathInfo={pathInfo} />
    </>
  );
}

export const fetchProps = async ({ resort }: { region: string, resort: string }): Promise<ResortWeatherPageProps> => {
  const cache: AxiosResponse<IResortPathInfo> = await assertShouldBeNot404(() => getResortSlug(resort));
  const { uuid } = cache.data;
  const res = await fetchResortShortInfo(uuid);
  const fullResort = await fetchResortSnowReport(uuid);
  const weatherInfoDaily = await fetchWeatherInfo(uuid, ResortWeatherType.daily);
  const weatherInfoHourly = await fetchWeatherInfo(uuid, ResortWeatherType.hourly);
  const relatedNews = await fetchResortRelatedNews(uuid);

  return {
    type: 'resort',
    route: 'weather',
    resort: res,
    shortWeather: await fetchResortShortWeatherReport(uuid),
    fullResort,
    pathInfo: cache.data,
    weatherInfoDaily,
    weatherInfoHourly,
    relatedNews,

    /**
       * Layout
       */
    related: await fetchResortRelated(uuid),
    navigation: {
      link: CurrentLink.WEATHER,
      tab: CurrentTab.SNOW,
      head: HeaderNavCurLink.SNOW_REPORTS,
    },
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    adTKey: 'resort weather',
    hero: {
      title: 'resort.weather.heroTitle',
      showVideo: false,
    },
    meta: {
      description: 'Resort.weather.description',
      redirectTo: cache.data.domain ?? null,
      title: 'Resort.weather.title',
      dynamic: (await fetchResortMeta(uuid)),
    },
    accommodations: await fetchResortAccommodations(uuid),

  };
};

// This function gets called at build time
export const getResortWeatherPageStaticProps = wrapStaticProps(fetchProps);
export const getResortWeatherPageServerSideProps = wrapServerSideProps(fetchProps);
